import { useState, useCallback, useEffect } from 'react';
import { apiClient, FileMetadata } from '../services';

export const useFileManager = () => {
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileError, setFileError] = useState<string | null>(null);

  const loadFiles = useCallback(async () => {
    try {
      const fileList = await apiClient.listFiles();
      setFiles(fileList);
    } catch (error) {
      console.error('Error loading files:', error);
      setFileError('Failed to load files');
    }
  }, []);

  const uploadFile = useCallback(async (file: File) => {
    setIsLoadingFiles(true);
    setUploadProgress(0);
    setFileError(null);

    try {
      await apiClient.uploadFileOnly(file, (progress) => {
        setUploadProgress(progress);
      });

      await loadFiles();
      setUploadProgress(100);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload file';
      setFileError(errorMessage);
      throw error;
    } finally {
      setIsLoadingFiles(false);
    }
  }, [loadFiles]);

  const deleteFile = useCallback(async (fileId: string) => {
    try {
      await apiClient.deleteFile(fileId);
      if (selectedFileId === fileId) {
        setSelectedFileId(null);
      }
      await loadFiles();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete file';
      setFileError(errorMessage);
      throw error;
    }
  }, [selectedFileId, loadFiles]);

  const selectFile = useCallback((fileId: string) => {
    setSelectedFileId(fileId);
    setFileError(null);
  }, []);

  const getSelectedFileMetadata = useCallback(() => {
    if (!selectedFileId) return null;
    return files.find(f => f.file_id === selectedFileId) || null;
  }, [selectedFileId, files]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  return {
    files,
    selectedFileId,
    isLoadingFiles,
    uploadProgress,
    fileError,
    uploadFile,
    deleteFile,
    selectFile,
    getSelectedFileMetadata,
    loadFiles,
  };
};